import PageMeta from "../components/PageMeta";
import { Plugs, Envelope, Trash } from "@phosphor-icons/react";

/**
 * Data Deletion page — a Google OAuth verification requirement for
 * apps using sensitive scopes. Must be publicly findable (linked in
 * the footer of every page) so users can find out how to revoke our
 * access and delete data we hold.
 */
export default function DataDeletion() {
  return (
    <>
      <PageMeta
        title="Data deletion · TruckTrackr"
        description="How to disconnect Google Calendar or your Facebook Page from TruckTrackr and how to delete your TruckTrackr account."
        canonical="https://thetrucktrackr.com/data-deletion"
      />
      <article
        className="mx-auto max-w-3xl px-5 py-10 legal-prose"
        data-testid="data-deletion-page"
      >
        <h1 className="font-display">Data deletion</h1>
        <p className="text-sm text-muted-foreground">
          Last updated: June 1, 2026
        </p>

        <p>
          This page explains how to disconnect third-party integrations
          and how to delete your TruckTrackr account and the data we
          hold about you. We process deletion requests within 30 days.
        </p>

        <h2 className="flex items-center gap-2">
          <Plugs weight="duotone" /> Disconnect Google Calendar
        </h2>
        <ol>
          <li>
            Sign in at{" "}
            <a href="https://thetrucktrackr.com/login">thetrucktrackr.com</a>.
          </li>
          <li>Open your <a href="/dashboard">Dashboard</a>.</li>
          <li>
            Scroll to the <strong>Google Calendar</strong> card.
          </li>
          <li>
            Click <strong>Disconnect</strong> and confirm.
          </li>
        </ol>
        <p>
          When you disconnect:
        </p>
        <ul>
          <li>
            We revoke our OAuth token with Google immediately.
          </li>
          <li>
            We delete the stored access token, refresh token, and all
            calendar-derived event records within 30 days.
          </li>
          <li>
            Truck appearances we previously published on TruckTrackr from
            your calendar events are removed from public view.
          </li>
        </ul>

        <p>
          You can also revoke our access directly from Google at any time
          at{" "}
          <a
            href="https://myaccount.google.com/permissions"
            target="_blank"
            rel="noopener noreferrer"
          >
            myaccount.google.com/permissions
          </a>
          .
        </p>

        <h2 className="flex items-center gap-2">
          <Plugs weight="duotone" /> Disconnect your Facebook Page
        </h2>
        <ol>
          <li>
            Sign in at{" "}
            <a href="https://thetrucktrackr.com/login">thetrucktrackr.com</a>.
          </li>
          <li>Open your <a href="/dashboard">Dashboard</a>.</li>
          <li>
            Find the <strong>Facebook Page</strong> card.
          </li>
          <li>
            Click <strong>disconnect</strong>.
          </li>
        </ol>
        <p>When you disconnect:</p>
        <ul>
          <li>
            We delete the stored Facebook access tokens and the connected
            Page's name/ID within 30 days.
          </li>
          <li>Auto-posting to your Page stops immediately.</li>
        </ul>
        <p>
          You can also revoke TruckTrackr's access from Facebook directly:
          on Facebook go to <strong>Settings &amp; privacy → Settings →
          Business integrations</strong>, find TruckTrackr, and remove it.
          To request deletion of any data we received from Facebook, email{" "}
          <a href="mailto:support@thetrucktrackr.com?subject=Facebook%20data%20deletion%20request">
            support@thetrucktrackr.com
          </a>{" "}
          with the subject "Facebook data deletion request" — we process
          these within 30 days.
        </p>

        <h2 className="flex items-center gap-2">
          <Trash weight="duotone" /> Delete your TruckTrackr account
        </h2>
        <p>To permanently delete your account and associated data:</p>
        <ol>
          <li>
            Email{" "}
            <a href="mailto:support@thetrucktrackr.com?subject=Account%20deletion%20request">
              support@thetrucktrackr.com
            </a>{" "}
            from the email address on your TruckTrackr account.
          </li>
          <li>Use the subject line "Account deletion request".</li>
          <li>
            We'll reply within 2 business days to confirm and process the
            request.
          </li>
        </ol>

        <p>
          When you delete your account we erase:
        </p>
        <ul>
          <li>Your account profile, email, and password hash.</li>
          <li>
            For owners: your truck profile, menu, photos, schedule, and
            all published appearances.
          </li>
          <li>
            Any Google-derived data we hold (OAuth tokens, calendar event
            metadata).
          </li>
          <li>Push notification subscriptions and follow lists.</li>
          <li>
            Analytics events tied to your account identifier (we may
            retain fully-aggregated, non-identifiable metrics).
          </li>
        </ul>

        <p>
          Deletion is permanent and cannot be undone. Backups containing
          your data are rotated out within 30 days.
        </p>

        <h2 className="flex items-center gap-2">
          <Envelope weight="duotone" /> Other data requests
        </h2>
        <p>
          To request a copy of the data we hold about you, to correct
          inaccurate data, or to ask about how your data is used, email{" "}
          <a href="mailto:support@thetrucktrackr.com">
            support@thetrucktrackr.com
          </a>
          .
        </p>

        <p>
          For full details, see our{" "}
          <a href="/privacy">Privacy Policy</a>.
        </p>
      </article>
    </>
  );
}
